import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { queryFetchJobApplications } from '../api/myJobsApi';
import { myJobsKeys } from '../query/myJobsKeys';
import { JobApplication, JobStatus, SendingStatus } from '../types';
import { isInPast } from '../utils';
import { isActiveStatus, mapJobStatusToSendingStatuses } from '../utils/statusMapper';

export interface UseMyJobsOptions {
  statuses?: JobStatus[];
  pageSize?: number;
  enabled?: boolean;
  autoLoadUntilPast?: boolean;
}

const STOP_AUTO_LOAD_AT_PAGE = 3;

const ALL_SENDING_STATUSES: SendingStatus[] = [
    'Accepted',
    'Assigned',
    'Rejected',
    'Registered',
    'SentToCustomer',
    'RejectedByCustomer'
];

/**
 * Smart Pagination Hook for My Jobs
 *
 * This hook implements intelligent pagination to ensure all future/active jobs are displayed
 * while maintaining optimal performance for the majority of users.
 *
 * **Problem:**
 * - Backend sorts jobs by date DESC (newest first)
 * - Backend doesn't support date filtering, only status filtering
 * - Standard pagination might miss future jobs if candidate has >100 total jobs
 *
 * **Solution:**
 * - Automatically load additional pages until we find past jobs
 * - Show "Load More" button for accessing full history
 *
 * **How it works:**
 * 1. Load page 1 (100 items by default)
 * 2. Check if any jobs are "past" (dateEndUtc < now)
 * 3. If NO past jobs AND page is full → load next page
 * 4. Stop when: past jobs found OR incomplete page OR safety limit (5 pages)
 * 5. Show "Load More" button if jobs.length is multiple of pageSize
 *
 * **Edge Cases:**
 * - >100 future jobs (rare): Auto-loads pages 2-5 until finding past jobs
 * - Exactly 100/200/300... jobs: Shows "Load More" button
 * - All past jobs: Stops immediately at page 1
 * - Backend bug (no past jobs ever): Safety limit prevents infinite loading (max 300 items)
 *
 * **Performance:**
 * - 99%+ users: Single page load (no change from before)
 * - 0.9% users: 2-3 page auto-load (transparent UX)
 * - 0.1% users: 4-5 page auto-load + manual "Load More"
 */
export function useMyJobs(options: UseMyJobsOptions = {}) {
    const {
        statuses,
        pageSize = 100,
        enabled = true,
        autoLoadUntilPast = true
    } = options;

    // State for accumulated jobs across pages
    const [ accumulatedJobs, setAccumulatedJobs ] = useState<JobApplication[]>([]);
    const [ currentPage, setCurrentPage ] = useState(1);
    const [ hasReachedPast, setHasReachedPast ] = useState(false);

    const sendingStatuses = useMemo(
        () => {
            const apiStatuses: SendingStatus[] = statuses
                ? statuses.flatMap(status => mapJobStatusToSendingStatuses(status))
                : ALL_SENDING_STATUSES;

            return Array.from(new Set(apiStatuses)).sort();
        },
        [ statuses ]
    );

    const query = useQuery({
        queryKey: myJobsKeys.list(sendingStatuses, currentPage, pageSize),
        queryFn: queryFetchJobApplications({
            pageNum: currentPage,
            pageSize,
            statuses: sendingStatuses
        }),
        enabled: enabled && !hasReachedPast
    });

    const countersMap = useMemo(() => {
        const map: Partial<Record<JobStatus, number>> & { ExternalJob?: number } = { };

        return map;
    }, [ ]);

    // Reset state when statuses change
    const statusesKey = sendingStatuses.join(',');

    useEffect(() => {
        setAccumulatedJobs([]);
        setCurrentPage(1);
        setHasReachedPast(false);
    }, [ statusesKey ]);

    // Check if page contains past jobs and handle auto-pagination
    useEffect(() => {
        if (!query.data || query.data.items.length === 0) {
            return;
        }

        const hasPastJobs = query.data.items.some(job => isInPast(job));
        const isLastPage = query.data.items.length < pageSize;

        if (hasPastJobs || isLastPage) {
            // Stop loading: found past jobs OR reached last page
            setHasReachedPast(true);
            setAccumulatedJobs(prev => {
                // Avoid duplicates if page was already added
                const existingIds = new Set(prev.map(j => j.id));
                const newJobs = fixStatusesForTab(statuses, query.data.items.filter(j => !existingIds.has(j.id)));

                return [ ...prev, ...newJobs ];
            });
        } else if (autoLoadUntilPast && currentPage < STOP_AUTO_LOAD_AT_PAGE) {
            // Continue loading next page (max 3 pages for safety = 300 items)
            setAccumulatedJobs(prev => {
                const existingIds = new Set(prev.map(j => j.id));
                const newJobs = fixStatusesForTab(statuses, query.data.items.filter(j => !existingIds.has(j.id)));

                return [ ...prev, ...newJobs ];
            });
            setCurrentPage(prev => prev + 1);
        } else {
            // Safety limit reached (3 pages) or autoLoadUntilPast disabled
            // Stop auto-pagination to prevent infinite loading in case of backend bug
            setHasReachedPast(true);
            setAccumulatedJobs(prev => {
                const existingIds = new Set(prev.map(j => j.id));
                const newJobs = fixStatusesForTab(statuses, query.data.items.filter(j => !existingIds.has(j.id)));

                return [ ...prev, ...newJobs ];
            });
        }
    }, [ query.data, autoLoadUntilPast, pageSize, currentPage ]);

    // Manual "Load More" function
    const loadMore = useCallback(() => {
        if (query.isLoading) {return;}

        setHasReachedPast(false); // Allow loading next page
        setCurrentPage(prev => prev + 1);
    }, [ query.isLoading ]);

    // Filter and transform accumulated jobs
    const filteredData = useMemo(() => {
        if (accumulatedJobs.length === 0) {return [];}

        return accumulatedJobs.map(job => {
            // For confirmed jobs, filter to show only active shifts/periods
            // For applied/rejected jobs, show all shifts/periods
            if (isActiveStatus(job.status) && statuses?.flatMap(s => mapJobStatusToSendingStatuses(s)).some(s => isActiveStatus(s))) {
                const activeShifts = job.shifts.filter(s => isActiveStatus(s.statusForCandidate));
                const activePeriods = job.periods.filter(s => isActiveStatus(s.statusForCandidate));

                const startDate = activeShifts.map(s => s.start).concat(activePeriods.map(s => s.start)).sort()[0];
                const endDate = activeShifts.map(s => s.finish).concat(activePeriods.map(s => s.finish)).sort().reverse()[0];

                return {
                    ...job,
                    shifts: activeShifts,
                    periods: activePeriods,
                    dateStartUtc: startDate ?? job.dateStartUtc,
                    dateEndUtc: endDate ?? job.dateEndUtc
                } as JobApplication;
            }

            return job;
        });
    }, [ accumulatedJobs ]);

    // Determine if "Load More" button should show
    const canLoadMore = useMemo(() => {
        return (
            hasReachedPast &&
            accumulatedJobs.length > 0 &&
            accumulatedJobs.length % pageSize === 0
        );
    }, [ hasReachedPast, accumulatedJobs.length, pageSize ]);

    const refetch = useCallback(() => {
        setCurrentPage(1);
        setAccumulatedJobs([]);
        setHasReachedPast(false);
        query.refetch();
    }, [ query ]);

    return {
        data: filteredData,
        isLoading: query.isLoading && currentPage === 1,
        isLoadingMore: query.isLoading && currentPage > 1,
        isError: query.isError,
        canLoadMore,
        countersMap,
        loadMore,
        refetch
    };
}

/** Temporary fix to show only data applicable to current tab (in case of jobs of mixed statuses) */
function fixStatusesForTab(selectedStatuses: JobStatus[] | undefined, jobs: JobApplication[]): JobApplication[] {
    if (selectedStatuses?.length !== 1) {
        return jobs;
    }

    const selectedStatus = selectedStatuses[0];

    return jobs.map(job => {
        const sendingStatuses = mapJobStatusToSendingStatuses(selectedStatus);

        if (sendingStatuses.length === 0) {
            return job;
        }

        return {
            ...job,
            status: sendingStatuses[0],
            shifts: job.shifts.filter(shift => sendingStatuses.includes(shift.statusForCandidate)),
            periods: job.periods.filter(period => sendingStatuses.includes(period.statusForCandidate))
        };
    });
}
